import React, { FC } from 'react';
import { Charge, ChargeLog, ChargeTypes, ItemLog, ReservationItemModel } from '../../../api/reservation/reservation.types';
import { useTranslation } from 'react-i18next';


interface ChargesTableProps {
  chargeLog: ChargeLog;
  mandatoryItems: ItemLog;
  optionalItems: ItemLog;
  conditionalItems: ItemLog;
  inclusiveItems: ItemLog;
}

export const ChargesTable: FC<ChargesTableProps> = (props) => {
  const { t } = useTranslation();

  const getChargeByType = (charges: Charge[], type: ChargeTypes) => charges.find((c) => c.type === type) || { amount: 0 };

  const getChargeInfo = () => {
    const charges: Charge[] = props.chargeLog.charges;
    const totalTaxes = getChargeByType(charges, ChargeTypes.ESTIMATED_TOTAL_TAX);
    const totalPrice = getChargeByType(charges, ChargeTypes.ESTIMATED_TOTAL_NET);
    const collectByBroker = getChargeByType(charges, ChargeTypes.COLLECT_NOW_W_COMMISSION_TOTAL);
    const onPickup = getChargeByType(charges, ChargeTypes.TOTAL_PAY_LOCALLY);
    const carAdjustmentTax = getChargeByType(charges, ChargeTypes.ADJUSTED_VEHICLE_BASE_RATE_TAX);
    const carAdjustmentPrice = getChargeByType(charges, ChargeTypes.ADJUSTED_VEHICLE_BASE_RATE_NET);
    const carAdjustmentTotal = getChargeByType(charges, ChargeTypes.ADJUSTED_VEHICLE_BASE_RATE_TOTAL);
    const carBrokerCommissionTax = getChargeByType(charges, ChargeTypes.BROKER_COMMISSION_TAX);
    const carBrokerCommissionPrice = getChargeByType(charges, ChargeTypes.BROKER_COMMISSION_NET);
    const carBrokerCommissionTotal = getChargeByType(charges, ChargeTypes.BROKER_COMMISSION_TOTAL);
    const carPayLocallyTax = getChargeByType(charges, ChargeTypes.PAY_LOCALLY_FEE_TAX);
    const carPayLocallyPrice = getChargeByType(charges, ChargeTypes.PAY_LOCALLY_FEE_NET);
    const carPayLocallyTotal = getChargeByType(charges, ChargeTypes.PAY_LOCALLY_FEE_TOTAL);
    const promoCodeTax = getChargeByType(charges, ChargeTypes.PROMO_CODE_DISCOUNT_TAX);
    const promoCodeSaf = getChargeByType(charges, ChargeTypes.PROMO_CODE_DISCOUNT_SAF);
    const promoCodePrice = getChargeByType(charges, ChargeTypes.PROMO_CODE_DISCOUNT_NET);
    const promoCodeTotal = getChargeByType(charges, ChargeTypes.PROMO_CODE_DISCOUNT_TOTAL);
    const airportTax = getChargeByType(charges, ChargeTypes.ESTIMATED_TOTAL_SAF);
    const carModelSaf = getChargeByType(charges, ChargeTypes.ADJUSTED_VEHICLE_BASE_RATE_SAF);
    const totalAmount = charges.find((c) => c.type === ChargeTypes.ESTIMATED_TOTAL_AMOUNT);
    const mandatoryItemTotal = getChargeByType(charges, ChargeTypes.MANDATORY_ITEM_TOTAL);
    const mandatoryItemSaf = getChargeByType(charges, ChargeTypes.MANDATORY_ITEM_SAF);
    const mandatoryItemTax = getChargeByType(charges, ChargeTypes.MANDATORY_ITEM_TAX);
    const mandatoryItemNet = getChargeByType(charges, ChargeTypes.MANDATORY_ITEM_NET);

    return {
      totalTax: totalTaxes.amount,
      totalPrice: totalPrice.amount,
      totalAmount: totalAmount ? totalAmount.amount : 0,
      collectByBroker: collectByBroker.amount,
      onPickup: onPickup.amount,
      currency: totalAmount ? totalAmount.currency : 'EUR',
      carModelTax: carAdjustmentTax.amount + carBrokerCommissionTax.amount,
      carModelPrice: carAdjustmentPrice.amount + carBrokerCommissionPrice.amount,
      carModelTotal: carAdjustmentTotal.amount + carBrokerCommissionTotal.amount,
      promoCodeTax: promoCodeTax.amount,
      promoCodeSaf: promoCodeSaf.amount,
      promoCodePrice: promoCodePrice.amount,
      promoCodeTotal: promoCodeTotal.amount,
      airportTax: airportTax.amount,
      carModelSaf: carModelSaf.amount,
      payLocallyPrice: carPayLocallyPrice.amount,
      payLocallyTax: carPayLocallyTax.amount,
      payLocallyTotal: carPayLocallyTotal.amount,
      vehicleRateAfterDiscount: carAdjustmentTotal.amount + carBrokerCommissionTotal.amount - promoCodeTotal.amount + mandatoryItemTotal.amount,
      vehicleRateAfterDiscountSaf: carModelSaf.amount - promoCodeSaf.amount + mandatoryItemSaf.amount,
      vehicleRateAfterDiscountTax: carAdjustmentTax.amount + carBrokerCommissionTax.amount - promoCodeTax.amount + mandatoryItemTax.amount,
      vehicleRateAfterDiscountPrice: carAdjustmentPrice.amount + carBrokerCommissionPrice.amount - promoCodePrice.amount + mandatoryItemNet.amount
    };
  };

  const extractItem = (itemLog: ItemLog) => {
    const items: ReservationItemModel[] = [];
    itemLog.extras.forEach(extra => items.push(extra));
    itemLog.coverages.forEach(coverage => items.push(coverage));
    itemLog.fees.forEach(fee => items.push(fee));
    return items;
  };

  const mandatoryItems = extractItem(props.mandatoryItems);
  const inclusiveItems = extractItem(props.inclusiveItems);
  const conditionalItems = extractItem(props.conditionalItems);
  const optionalItems = extractItem(props.optionalItems);


  const charges = getChargeInfo();
  const showAirportTax = !!(charges.airportTax && charges.airportTax !== 0);

  const tableHeaders = (header: string, hasQuantity: boolean) => (
    <thead className="-header">
    <tr>
      <th colSpan={6}>
        {header}
      </th>
    </tr>
    <tr>
      <th colSpan={hasQuantity ? 1 : 2} className="name">{t('ct.reservation.charge')}</th>
      {hasQuantity && (
        <th className="quantity numeric-column">{t('ct.reservation.quantity')}</th>
      )}
      <th className="numeric-column">{t('ct.reservation.price')}</th>
      {showAirportTax && (
        <th className="numeric-column">{t('ct.reservation.airport_tax')}</th>
      )}
      <th className="numeric-column">{t('ct.reservation.tax')}</th>
      <th className="numeric-column">{t('ct.reservation.total')}</th>
    </tr>
    </thead>
  );

  const getPricesSection = (prices: ReservationItemModel[], hasPrice: boolean, hasQuantity?: boolean) => {
    const tableRows: any[] = [];
    prices.forEach((p: ReservationItemModel) => tableRows.push(
      <tr>
        <td colSpan={hasQuantity ? 1 : 2} className="name">{p.name}</td>
        {hasQuantity && (
          <td className="quantity numeric-column">{p.quantity ?? ''}</td>
        )}
        <td className="numeric-column">{getPrice(p.net, hasPrice)}</td>
        {showAirportTax && (
          <td className="numeric-column">{getPrice(p.saf, hasPrice)}</td>
        )}
        <td className="numeric-column">{getPrice(p.tax, hasPrice)}</td>
        <td className="numeric-column">{getPrice(p.price, hasPrice)}</td>
      </tr>
    ));

    return tableRows;
  };

  const getPrice = (price: number, hasPrice?: boolean) => {
    if (hasPrice) {
      return price ? price.toFixed(2) : '0.00';
    }
    return '';
  };

  const getPayLocally = () => {
    return charges.payLocallyPrice ? (
      <tr>
        <td className="first-col name" colSpan={2}>{t('ct.reservation.pay_locally_markup')}</td>
        <td
          className="numeric-column">{charges.payLocallyPrice.toFixed(2)}</td>
        {showAirportTax && (
          <td className="numeric-column"></td>
        )}
        <td className="numeric-column">{charges.payLocallyTax.toFixed(2)}</td>
        <td
          className="numeric-column">{charges.payLocallyTotal.toFixed(2)}</td>
      </tr>
    ) : null;
  };

  const getPromoCode = () => {
    return charges.promoCodePrice ? (
      <tr>
        <td className="first-col name" colSpan={2}>{t('ct.reservation.promo_discount')}</td>
        <td
          className="numeric-column">-{charges.promoCodePrice.toFixed(2)}</td>
        {showAirportTax && (
          <td className="numeric-column">-{charges.promoCodeSaf.toFixed(2)}</td>
        )}
        <td className="numeric-column">-{charges.promoCodeTax.toFixed(2)}</td>
        <td
          className="numeric-column">-{charges.promoCodeTotal.toFixed(2)}</td>
      </tr>
    ) : null;
  };

  const getVehicleBaseRate = () => {
    return (
      <tr>
        <td className="first-col name" colSpan={2}>{t('rt.reservation.car_rate')}</td>
        <td
          className="numeric-column">{charges.carModelPrice ? charges.carModelPrice.toFixed(2) : '0.00'}</td>
        {showAirportTax && (
          <td className="numeric-column">{charges.carModelSaf ? charges.carModelSaf.toFixed(2) : '0.00'}</td>
        )}
        <td className="numeric-column">{charges.carModelTax ? charges.carModelTax.toFixed(2) : '0.00'}</td>
        <td
          className="numeric-column">{charges.carModelTotal ? charges.carModelTotal.toFixed(2) : '0.00'}</td>
      </tr>
    );
  };

  const getVehicleRateAfterDiscount = () => {
    return (
      <tr className="total">
        <td className="first-col name" colSpan={2}>{t('ct.reservation.vehicle_rate_after_discount')}</td>
        <td
          className="numeric-column">{charges.vehicleRateAfterDiscountPrice ? charges.vehicleRateAfterDiscountPrice.toFixed(2) : '0.00'}</td>
        {showAirportTax && (
          <td
            className="numeric-column">{charges.vehicleRateAfterDiscountSaf ? charges.vehicleRateAfterDiscountSaf.toFixed(2) : '0.00'}</td>
        )}
        <td
          className="numeric-column">{charges.vehicleRateAfterDiscountTax ? charges.vehicleRateAfterDiscountTax.toFixed(2) : '0.00'}</td>
        <td
          className="numeric-column">{charges.vehicleRateAfterDiscount ? charges.vehicleRateAfterDiscount.toFixed(2) : '0.00'}</td>
      </tr>
    );
  };


  return (
    <div className="d-flex col-12 justify-content-center mt-4">
      <div className="col-8">
        <table className="ReactTable charges-table">
          {tableHeaders(t('ct.reservation.vehicle_charge'), false)}
          <tbody>
          {getVehicleBaseRate()}
          {getPricesSection(mandatoryItems, true)}
          {getPricesSection(inclusiveItems, false)}
          {getPromoCode()}
          {getVehicleRateAfterDiscount()}
          </tbody>
          {(conditionalItems.length > 0 || !!charges.payLocallyPrice) && (
            <>
              <tr className="empty-row">
                <td colSpan={6}></td>
              </tr>
              {tableHeaders(t('ct.reservation.conditional_charges'), false)}
              <tbody>
              {getPricesSection(conditionalItems, true)}
              {getPayLocally()}
              </tbody>
            </>
          )}
          {optionalItems.length > 0 && (
            <>
              <tr className="empty-row">
                <td colSpan={6}></td>
              </tr>
              {tableHeaders(t('ct.reservation.optional_charges'), true)}
              <tbody>
              {getPricesSection(optionalItems, true, true)}
              </tbody>
            </>
          )}
          <tbody>
          <tr className="empty-row">
            <td colSpan={6}></td>
          </tr>
          <tr className="total">
            <td className="empty-cell" colSpan={showAirportTax ? 3 : 2}></td>
            <td colSpan={2} className="numeric-column"
            >{t('ct.reservation.total', { currency: charges.currency })}</td>
            <td className="numeric-column">{getPrice(charges.totalAmount, true)} {charges.currency}</td>
          </tr>
          <tr className="total">
            <td className="empty-cell" colSpan={showAirportTax ? 3 : 2}></td>
            <td className="numeric-column"
                colSpan={2}>{t('rt.reservation.collected_by_broker')}</td>
            <td className="numeric-column">{getPrice(charges.collectByBroker, true)} {charges.currency}</td>
          </tr>
          <tr className="total">
            <td className="empty-cell" colSpan={showAirportTax ? 3 : 2}></td>
            <td className="numeric-column"
                colSpan={2}>{t('rt.reservation.collect_on_pickup')}</td>
            <td className="numeric-column">{getPrice(charges.onPickup, true)} {charges.currency}</td>
          </tr>
          </tbody>
        </table>
      </div>

    </div>
  );
};